import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound,
  Copy,
  RefreshCw,
  Trash2,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { apiFetch } from "@/lib/api";

/** `twk_live` + `3f2a` → `twk_live_••••••••3f2a` (nunca o valor cru). */
function maskToken(token) {
  if (!token) return "";
  return `${token.prefix}_${"•".repeat(8)}${token.last4}`;
}

/**
 * CompanyPartnerSection — bloco "Parceria" do modal Editar Empresa.
 *
 * Dois estados independentes:
 *  - o switch (`isPartner`), que só é persistido quando o formulário inteiro
 *    é salvo (o pai controla `formData.isPartner`);
 *  - os controles de TOKEN DE PARCEIRO, que chamam a API diretamente
 *    (emitir/reemitir/revogar/copiar não esperam o Save).
 *
 * Por isso os botões de token ficam desabilitados quando o flag foi ligado
 * agora mas ainda não salvo (`isPartner && !savedIsPartner`) — o backend
 * (POST /api/internal/api-tokens/partner) exige `isPartner = true` já
 * gravado, e emitir antes daria 403 sem explicação nenhuma na tela.
 */
export default function CompanyPartnerSection({
  accountId,
  isPartner,
  savedIsPartner,
  onIsPartnerChange,
}) {
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const [confirmReemitOpen, setConfirmReemitOpen] = useState(false);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);

  const hasToken = !!tokenInfo;
  const notYetSaved = isPartner && !savedIsPartner;
  const canManageToken = isPartner && savedIsPartner;

  useEffect(() => {
    let cancelled = false;
    async function loadToken() {
      setLoadingInfo(true);
      try {
        const res = await apiFetch(
          `/api/internal/api-tokens/partner?accountId=${accountId}`,
        );
        if (!res.ok) throw new Error("Falha ao buscar o token de parceiro.");
        const data = await res.json();
        if (!cancelled) setTokenInfo(data.token);
      } catch (err) {
        console.error("Failed to load partner token", err);
      } finally {
        if (!cancelled) setLoadingInfo(false);
      }
    }
    loadToken();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  function handleSwitchChange(next) {
    if (!next && hasToken) {
      setConfirmDisableOpen(true);
      return;
    }
    onIsPartnerChange(next);
  }

  async function issueToken() {
    setBusy(true);
    try {
      const res = await apiFetch("/api/internal/api-tokens/partner", {
        method: "POST",
        body: JSON.stringify({ accountId }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Erro ao emitir token de parceiro.");
      }
      setTokenInfo(data.token);
      toast.success("Token de parceiro emitido.");
    } catch (err) {
      toast.error(err.message);
      throw err;
    } finally {
      setBusy(false);
    }
  }

  async function handleIssueClick() {
    if (hasToken) {
      setConfirmReemitOpen(true);
      return;
    }
    await issueToken();
  }

  async function handleRevoke() {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/api/internal/api-tokens/partner?accountId=${accountId}`,
        { method: "DELETE" },
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Erro ao revogar token de parceiro.");
      }
      setTokenInfo(null);
      toast.success("Token de parceiro revogado.");
    } catch (err) {
      toast.error(err.message);
      throw err;
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    setBusy(true);
    try {
      const res = await apiFetch("/api/internal/api-tokens/partner/reveal", {
        method: "POST",
        body: JSON.stringify({ accountId }),
      });
      const data = await res.json();
      if (!res.ok || !data.token) {
        throw new Error(data.error || "Erro ao revelar token de parceiro.");
      }
      await navigator.clipboard.writeText(data.token);
      toast.success("Token copiado.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <label className="flex items-center gap-1.5 text-sm font-medium text-foreground-secondary">
            <ShieldCheck className="w-4 h-4" />
            Conta parceira
          </label>
          <p className="text-xs text-foreground-muted mt-0.5">
            Permite emitir tokens de parceiro e acessar a API de onboarding.
            Não altera as contas filhas.
          </p>
        </div>
        <Switch
          aria-label="Conta parceira"
          checked={!!isPartner}
          onCheckedChange={handleSwitchChange}
        />
      </div>

      <div className="pt-3 border-t border-border">
        <label className="flex items-center gap-1.5 text-xs font-medium text-foreground-secondary uppercase tracking-wide mb-2">
          <KeyRound className="w-3.5 h-3.5" />
          Token de parceiro
        </label>

        {!canManageToken ? (
          <p className="text-xs text-foreground-muted">
            {notYetSaved
              ? "Salve a conta para emitir o token."
              : "Ative a parceria para habilitar o token."}
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={
                  loadingInfo
                    ? "Carregando…"
                    : hasToken
                      ? maskToken(tokenInfo)
                      : "Nenhum token emitido."
                }
                className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground-strong font-mono"
              />
              <button
                type="button"
                title="Copiar"
                aria-label="Copiar token"
                disabled={!hasToken || busy || loadingInfo}
                onClick={handleCopy}
                className="p-2 rounded-lg border border-border text-foreground-muted hover:text-foreground hover:border-border-strong transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy || loadingInfo}
                onClick={handleIssueClick}
                className="flex-1 py-2 text-sm rounded-lg border border-cyan-500 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {hasToken ? "Reemitir token" : "Emitir token"}
              </button>
              {hasToken && (
                <button
                  type="button"
                  disabled={busy || loadingInfo}
                  onClick={() => setConfirmRevokeOpen(true)}
                  className="py-2 px-3 text-sm rounded-lg border border-red-500/40 text-red-700 dark:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  Revogar
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDisableOpen}
        onOpenChange={setConfirmDisableOpen}
        onConfirm={() => onIsPartnerChange(false)}
        title="Desativar a parceria?"
        description="O token de parceiro desta conta será revogado imediatamente e as integrações que o utilizam deixarão de funcionar. As contas filhas não são alteradas."
        confirmLabel="Desativar e revogar"
      />
      <ConfirmDialog
        isOpen={confirmReemitOpen}
        onOpenChange={setConfirmReemitOpen}
        onConfirm={issueToken}
        title="Reemitir o token de parceiro?"
        description="O token atual será revogado e um novo será gerado. Qualquer integração usando o valor anterior para de funcionar imediatamente."
        confirmLabel="Reemitir"
        variant="default"
      />
      <ConfirmDialog
        isOpen={confirmRevokeOpen}
        onOpenChange={setConfirmRevokeOpen}
        onConfirm={handleRevoke}
        title="Revogar o token de parceiro?"
        description="Qualquer integração usando este token para de funcionar imediatamente."
        confirmLabel="Revogar"
      />
    </div>
  );
}
